import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables');
}

// Create a single instance of the Supabase client
export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce'
  },
  realtime: {
    params: {
      eventsPerSecond: 1
    }
  },
  db: {
    schema: 'public'
  },
  global: {
    fetch: async (...args) => {
      const maxRetries = 1;
      let attempt = 0;
      
      while (attempt < maxRetries) {
        try {
          const response = await fetch(...args);
          if (!response.ok) {
            // Only retry on specific status codes
            if ([408, 429, 500, 502, 503, 504].includes(response.status)) {
              attempt++;
              if (attempt < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
                continue;
              }
            }
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response;
        } catch (error) {
          if (attempt === maxRetries - 1) {
            console.error('Supabase fetch error:', {
              message: error.message,
              url: args[0],
              method: args[1]?.method
            });
            throw error;
          }
          attempt++;
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
      }
    }
  },
  storageKey: 'teacher-reporter-auth',
  storage: {
    getItem: (key) => {
      try {
        const item = window.sessionStorage.getItem(key);
        if (!item) return null;
        try {
          return JSON.parse(item);
        } catch {
          // If parsing fails, remove the corrupted item
          window.sessionStorage.removeItem(key);
          return null;
        }
      } catch (error) {
        console.error('Error reading from sessionStorage:', error);
        return null;
      }
    },
    setItem: (key, value) => {
      try {
        window.sessionStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        console.error('Error writing to sessionStorage:', error);
      }
    },
    removeItem: (key) => {
      try {
        window.sessionStorage.removeItem(key);
      } catch (error) {
        console.error('Error removing from sessionStorage:', error);
      }
    }
  }
});

// Add a retry mechanism for failed requests
export const retryableQuery = async (queryFn: () => Promise<any>, maxRetries = 3) => {
  let lastError;
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await queryFn();
    } catch (error) {
      lastError = error;
      if (i < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000));
      }
    }
  }
  throw lastError;
};